import { useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Icon } from '../design/index.js'
import { useApi } from '../app/api.js'
import { mailTemplatesApi, printTemplatesApi } from '../app/templates-api.js'
import './code-editor.css'

/**
 * Print, with a choice of layout (PRN-002).
 *
 * The button prints with the type's default layout, as it always did. The
 * arrow beside it lists every active layout for the type — "PO without Item
 * Code", "With Reference Numbers", the standard one — so a second layout is
 * one click away rather than an administrator's job. The list is fetched when
 * the arrow is opened, not with every document.
 */
export const PrintMenu = ({ docType, documentId, disabled, label = 'Print' }: {
  docType: string
  documentId: string
  disabled?: boolean
  label?: string
}) => {
  const api = useApi()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const layouts = useQuery({
    queryKey: ['layouts', api.companyId, docType],
    queryFn: () => printTemplatesApi(api).choices(docType),
    enabled: open,
  })
  // A PDF is offered only where the server can make one (PDF_CHROME_PATH).
  const capabilities = useQuery({
    queryKey: ['mail-capabilities'],
    queryFn: () => mailTemplatesApi(api).capabilities(),
    enabled: open,
  })

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  // Opened rather than fetched: the browser's own print dialog is what turns
  // it into a PDF, and it needs the page in a window to do that.
  const print = (template?: string) => window.open(api.printUrl(docType, documentId, template), '_blank', 'noopener')

  return (
    <div className="split-button" ref={root}>
      <Button kind="ghost" disabled={disabled} onClick={() => print()}>{label}</Button>
      <div className="menu">
        <button
          type="button"
          className="btn btn--ghost"
          aria-label="Choose a print layout"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="down" size={16} />
        </button>
        {open ? (
          <div className="menu__list" role="menu" id={menuId}>
            {layouts.isLoading ? <div className="menu__item">Loading layouts…</div> : null}
            {layouts.error ? <div className="menu__item">The layouts could not be listed.</div> : null}
            {(layouts.data ?? []).map((l) => (
              <button key={l.id} type="button" role="menuitem" className="menu__item"
                onClick={() => { setOpen(false); print(l.id) }}>
                {l.name}{l.isDefault ? ' — default' : ''}{l.isStandard ? ' (standard)' : ''}
              </button>
            ))}
            {capabilities.data?.pdf ? (
              <button type="button" role="menuitem" className="menu__item"
                onClick={() => { setOpen(false); window.location.assign(`${api.printUrl(docType, documentId)}?format=pdf`) }}>
                Download PDF
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
